export interface StoreFilterOptions {
  valueIds?: number[];
  searchTerm?: string;
}

export interface StoreFilterValue {
  code?: string;
  hexCode?: string | null;
  attributeDefinitionId: number;
  attributeValueId: number;
  attributeTitle: string;
  title: string;
}

export interface StoreFilterGroup {
  code?: string;
  attributeDefinitionId: number;
  title: string;
  values: { id: number; title: string; hexCode?: string | null }[];
}

// Read old bookmarks/storage only. New selections use valueIds for every attribute.
const legacyFilterKeys = [
  "paletteId", "backgroundColorId", "dimentionsId", "borderColorId",
  "reedId", "shapeId", "sizeTypeId",
];

function uniqueValueIds(values: unknown[]): number[] {
  return Array.from(new Set(values.map(Number).filter(function (id) {
    return Number.isSafeInteger(id) && id > 0;
  })));
}

export function normalizeStoreFilters(value: unknown): StoreFilterOptions {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const source = value as Record<string, unknown>;
  const selected: unknown[] = [];
  ["valueIds", ...legacyFilterKeys].forEach(function (key) {
    const stored = source[key];
    if (Array.isArray(stored)) selected.push(...stored);
    else if (stored !== undefined && stored !== null) selected.push(stored);
  });
  const valueIds = uniqueValueIds(selected);
  return {
    ...(valueIds.length ? { valueIds } : {}),
    ...(typeof source.searchTerm === "string" && source.searchTerm ? { searchTerm: source.searchTerm } : {}),
  };
}

export function filterValuesFromQuery(query: URLSearchParams): number[] {
  const selected = query.getAll("valueIds");
  legacyFilterKeys.forEach(function (key) {
    selected.push(...query.getAll(key[0].toUpperCase() + key.slice(1)));
  });
  return uniqueValueIds(selected);
}

export function createFilterValueSearchParams(filters: StoreFilterOptions): URLSearchParams {
  const query = new URLSearchParams();
  normalizeStoreFilters(filters).valueIds?.forEach(function (id) {
    query.append("valueIds", String(id));
  });
  return query;
}

export function groupStoreFilters(values: StoreFilterValue[]): StoreFilterGroup[] {
  const groups = new Map<number, StoreFilterGroup>();
  const seenValues = new Set<number>();
  values.forEach(function (value) {
    if (seenValues.has(value.attributeValueId)) return;
    seenValues.add(value.attributeValueId);
    let group = groups.get(value.attributeDefinitionId);
    if (!group) {
      group = { attributeDefinitionId: value.attributeDefinitionId, code: value.code, title: value.attributeTitle, values: [] };
      groups.set(value.attributeDefinitionId, group);
    }
    group.values.push({ id: value.attributeValueId, title: value.title, hexCode: value.hexCode });
  });
  return Array.from(groups.values());
}

export function selectedAttributeGroups(valueIds: number[], filters: StoreFilterGroup[]): number[][] {
  const attributeIds = new Map<number, number>();
  filters.forEach(function (filter) {
    filter.values.forEach(function (value) {
      attributeIds.set(value.id, filter.attributeDefinitionId);
    });
  });
  const groups = new Map<string, number[]>();
  valueIds.forEach(function (id) {
    const attributeId = attributeIds.get(id);
    const key = attributeId === undefined ? `missing:${id}` : String(attributeId);
    const group = groups.get(key) || [];
    group.push(id);
    groups.set(key, group);
  });
  return Array.from(groups.values());
}
